import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import * as yup from 'yup';

import { UnitNumberPattern } from '../../models/block.model';
import { addBlock, updateBlock } from '../../services/block.service';
import { formatUnitNumberPreview } from '../../utils/blockConfiguration';

type BlockForm = {
	number: string;
	levelFrom: string;
	levelTo: string;
	unitFrom: string;
	unitTo: string;
	unitNumberPattern: UnitNumberPattern;
};

const emptyBlockForm: BlockForm = {
	number: '',
	levelFrom: '1',
	levelTo: '',
	unitFrom: '',
	unitTo: '',
	unitNumberPattern: 'continuous',
};

const unitNumberPatternHelp: Record<UnitNumberPattern, string> = {
	continuous: 'Uses every unit number between the first and last unit numbers.',
	odd: 'Uses only odd unit numbers between the first and last unit numbers.',
	even: 'Uses only even unit numbers between the first and last unit numbers.',
};

const blockSchema = yup
	.object({
		precinctId: yup.number().integer().positive().required(),
		number: yup
			.string()
			.trim()
			.min(1, 'Enter a block number.')
			.max(5, 'Block number cannot be longer than 5 characters.')
			.required('Enter a block number.'),
		levelFrom: yup
			.number()
			.typeError('Enter the first residential level.')
			.integer('First residential level must be a whole number.')
			.positive('First residential level must be greater than 0.')
			.required(),
		levelTo: yup
			.number()
			.typeError('Enter the last residential level.')
			.integer('Last residential level must be a whole number.')
			.positive('Last residential level must be greater than 0.')
			.min(yup.ref('levelFrom'), 'Last residential level cannot be lower than the first residential level.')
			.required(),
		unitFrom: yup
			.number()
			.typeError('Enter the first unit number.')
			.integer('First unit number must be a whole number.')
			.positive('First unit number must be greater than 0.')
			.required(),
		unitTo: yup
			.number()
			.typeError('Enter the last unit number.')
			.integer('Last unit number must be a whole number.')
			.positive('Last unit number must be greater than 0.')
			.min(yup.ref('unitFrom'), 'Last unit number cannot be lower than the first unit number.')
			.required(),
		unitNumberPattern: yup.string().oneOf(['continuous', 'odd', 'even']).required(),
	})
	.test('unit-number-pattern', function (values) {
		if (!values) return true;
		if (values.unitNumberPattern === 'odd' && (values.unitFrom % 2 === 0 || values.unitTo % 2 === 0)) {
			return this.createError({
				path: 'unitFrom',
				message: 'First and last unit numbers must both be odd when using odd numbers only.',
			});
		}
		if (values.unitNumberPattern === 'even' && (values.unitFrom % 2 !== 0 || values.unitTo % 2 !== 0)) {
			return this.createError({
				path: 'unitFrom',
				message: 'First and last unit numbers must both be even when using even numbers only.',
			});
		}
		return true;
	});

export const AddEditBlockDialog = (props: any) => {
	const [loading, setLoading] = useState(false);
	const [form, setForm] = useState<BlockForm>(emptyBlockForm);

	useEffect(() => {
		if (!props.show) return;

		const block = props.isEditMode ? props.selectedBlock : null;
		setForm({
			number: block?.number ?? '',
			levelFrom: String(block?.levelFrom ?? 1),
			levelTo: block?.levelTo == null ? '' : String(block.levelTo),
			unitFrom: block?.unitFrom == null ? '' : String(block.unitFrom),
			unitTo: block?.unitTo == null ? '' : String(block.unitTo),
			unitNumberPattern: block?.unitNumberPattern ?? 'continuous',
		});
	}, [props.show, props.isEditMode, props.selectedBlock]);

	const unitPreview = useMemo(
		() => formatUnitNumberPreview(Number(form.unitFrom), Number(form.unitTo), form.unitNumberPattern),
		[form.unitFrom, form.unitTo, form.unitNumberPattern]
	);

	const updateForm = <Field extends keyof BlockForm>(field: Field, value: BlockForm[Field]) => {
		setForm((current) => ({ ...current, [field]: value }));
	};

	const onClose = () => {
		setLoading(false);
		props.close();
	};

	const handleSave = async () => {
		try {
			setLoading(true);
			const payload = await blockSchema.validate(
				{
					precinctId: props.precinctId,
					...form,
				},
				{ abortEarly: true, stripUnknown: true }
			);

			const result: any = props.isEditMode
				? await updateBlock('block/update', { blockId: props.selectedBlock?.id, ...payload })
				: await addBlock('block/add', payload);

			if (!result.data.success) {
				toast.error(result.data.message);
				return;
			}

			toast.success(result.data.message);
			props.onRefreshBlockPanel();
			onClose();
		} catch (error: any) {
			toast.error(error?.response?.data?.message || error?.message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={props.show} onClose={onClose} aria-labelledby='block-dialog-title'>
			<DialogTitle id='block-dialog-title'>{props.isEditMode ? 'Edit' : 'Add'} Block</DialogTitle>
			<DialogContent style={{ width: '420px' }}>
				<TextField
					margin='dense'
					label='Block Number'
					value={form.number}
					onChange={(event) => updateForm('number', event.target.value)}
					onBlur={() => updateForm('number', form.number.trim())}
					variant='standard'
					fullWidth
				/>
				<TextField
					margin='dense'
					type='number'
					label='First Residential Level'
					value={form.levelFrom}
					onChange={(event) => updateForm('levelFrom', event.target.value)}
					helperText='The first level that contains residential units.'
					inputProps={{ min: 1, step: 1 }}
					variant='standard'
					fullWidth
				/>
				<TextField
					margin='dense'
					type='number'
					label='Last Residential Level'
					value={form.levelTo}
					onChange={(event) => updateForm('levelTo', event.target.value)}
					inputProps={{ min: 1, step: 1 }}
					variant='standard'
					fullWidth
				/>
				<TextField
					margin='dense'
					type='number'
					label='First Unit Number'
					value={form.unitFrom}
					onChange={(event) => updateForm('unitFrom', event.target.value)}
					inputProps={{ min: 1, step: 1 }}
					variant='standard'
					fullWidth
				/>
				<TextField
					margin='dense'
					type='number'
					label='Last Unit Number'
					value={form.unitTo}
					onChange={(event) => updateForm('unitTo', event.target.value)}
					inputProps={{ min: 1, step: 1 }}
					variant='standard'
					fullWidth
				/>
				<FormControl fullWidth variant='standard' margin='dense'>
					<InputLabel id='unit-number-pattern-label'>Unit Numbers Used</InputLabel>
					<Select
						labelId='unit-number-pattern-label'
						value={form.unitNumberPattern}
						onChange={(event) => updateForm('unitNumberPattern', event.target.value as UnitNumberPattern)}>
						<MenuItem value='continuous'>Continuous</MenuItem>
						<MenuItem value='odd'>Odd numbers only</MenuItem>
						<MenuItem value='even'>Even numbers only</MenuItem>
					</Select>
					<FormHelperText>{unitNumberPatternHelp[form.unitNumberPattern]}</FormHelperText>
				</FormControl>
				{unitPreview && (
					<div style={{ marginTop: '12px', padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
						<div style={{ color: '#555', fontSize: '12px' }}>Unit number preview</div>
						<div style={{ marginTop: '3px', fontSize: '14px' }}>{unitPreview}</div>
					</div>
				)}
			</DialogContent>
			<DialogActions>
				{loading ? (
					<CircularProgress color='primary' size={28} />
				) : (
					<>
						<Button variant='outlined' size='small' onClick={onClose}>
							Cancel
						</Button>
						<Button type='submit' size='small' variant='contained' onClick={handleSave}>
							{props.isEditMode ? 'Update' : 'Add'}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
};
